import React from 'react';
import { jobAlerts } from '../data/jobAlerts';
import { Calendar, Briefcase as BriefcaseIcon, ArrowRight, Sparkles } from 'lucide-react';

const companyColors = {
  'Google': { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800', ring: 'ring-blue-200 dark:ring-blue-800' },
  'Microsoft': { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800', ring: 'ring-emerald-200 dark:ring-emerald-800' },
  'Amazon': { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800', ring: 'ring-amber-200 dark:ring-amber-800' },
  'default': { bg: 'bg-indigo-50 dark:bg-indigo-900/20', text: 'text-indigo-600 dark:text-indigo-400', border: 'border-indigo-200 dark:border-indigo-800', ring: 'ring-indigo-200 dark:ring-indigo-800' },
};

const getDeadlineInfo = (deadline) => {
  if (deadline === 'Open') return { text: 'Open', color: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/50' };
  const deadlineDate = new Date(deadline);
  const now = new Date();
  const diffDays = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { text: 'Expired', color: 'text-gray-400 bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700' };
  if (diffDays <= 7) return { text: `${diffDays}d left`, color: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50 animate-pulse' };
  if (diffDays <= 30) return { text: `${diffDays}d left`, color: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/50' };
  return { text: deadline, color: 'text-gray-500 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700' };
};

const Jobs = () => {
  return (
    <div className="space-y-10">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
            <BriefcaseIcon className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white font-display">Job & Internship Alerts</h2>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm pl-[52px]">Discover handpicked opportunities matching your skills.</p>
      </div>

      {/* Job Cards */}
      <div className="grid lg:grid-cols-2 gap-6 stagger-children">
        {jobAlerts.map((job, index) => {
          const colors = companyColors[job.company] || companyColors['default'];
          const deadlineInfo = getDeadlineInfo(job.deadline);

          return (
            <div
              key={index}
              className="glass-card-glow p-6 flex flex-col md:flex-row gap-5 items-start md:items-center hover:shadow-elevated transition-all duration-300 group"
            >
              {/* Company Avatar */}
              <div className={`w-14 h-14 rounded-2xl ${colors.bg} border ${colors.border} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                <span className={`text-xl font-extrabold ${colors.text}`}>
                  {job.company.charAt(0)}
                </span>
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1 font-display truncate">{job.role}</h3>
                <p className={`text-sm font-semibold ${colors.text} mb-3`}>{job.company}</p>
                
                <div className="flex flex-wrap gap-2">
                  {/* Job Type Badge */}
                  <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-lg border ${
                    job.type === 'Full-time' 
                      ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/50'
                      : 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/50'
                  }`}>
                    <BriefcaseIcon className="w-3 h-3" />
                    {job.type}
                  </span>

                  {/* Deadline Badge */}
                  <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-lg border ${deadlineInfo.color}`}>
                    <Calendar className="w-3 h-3" />
                    {deadlineInfo.text}
                  </span>
                </div>
              </div>

              {/* Apply Button */}
              <div className="w-full md:w-auto flex-shrink-0">
                <a 
                  href={job.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group/btn btn-primary w-full md:w-auto inline-flex items-center justify-center gap-2 py-3 px-6"
                >
                  Apply Now
                  <ArrowRight className="w-4 h-4 transform group-hover/btn:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {jobAlerts.length === 0 && (
        <div className="glass-card p-16 text-center space-y-4">
          <BriefcaseIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600" />
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">No openings right now</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Check back soon for new opportunities!</p>
        </div>
      )}

      {/* CTA Card */}
      <div className="glass-card p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 dark:from-indigo-900/10 dark:via-purple-900/10 dark:to-pink-900/10">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg shadow-indigo-500/20 flex-shrink-0">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h4 className="font-bold text-gray-900 dark:text-white font-display">Want personalized job alerts?</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">Complete your profile to get recommendations matching your skills.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Jobs;
